import Big from 'big.js';
import classNames from 'clsx';
import { useIntl } from 'react-intl';
import type { BillLimitDto } from '@ordr-as/ordr-service-types/epos';
import { LastResolvedBillLimitChangeDtoStatus } from '@ordr-as/ordr-service-types/epos';
import { Button } from '@ordr-as/umami-react/button';
import { useLocation } from '../../../../../../context/LocationContext';

interface BillLimitCardProps {
  billLimit: BillLimitDto;
  onEdit?: () => void;
  onCancelRequest?: () => void;
  isCancelRequestPending?: boolean;
  /** Cart context: the cart's items would push the bill past the limit. */
  showWillExceedWarning?: boolean;
}

const toConsumedPercentage = (consumedAmount: number, maxAmount: number) => {
  if (maxAmount <= 0) {
    return 100;
  }

  const percentage = new Big(consumedAmount).div(maxAmount).times(100).toNumber();
  return Math.min(100, Math.max(0, percentage));
};

interface ProgressBarProps {
  percentage: number;
  isMaxReached: boolean;
}

const ProgressBar = ({ percentage, isMaxReached }: ProgressBarProps) => (
  <div className="w-full rounded-full bg-brand-white">
    <div
      className={classNames('h-2 rounded-full', isMaxReached ? 'bg-orange' : 'bg-brand-primary')}
      style={{ width: `${percentage}%` }}
    />
  </div>
);

interface StatusMessageProps {
  billLimit: BillLimitDto;
  isMaxReached: boolean;
  showWillExceedWarning?: boolean;
}

/**
 * One status line: a pending change wins, then a guest rejection, then the max-reached
 * hint, then the cart's will-exceed warning.
 */
const StatusMessage = ({ billLimit, isMaxReached, showWillExceedWarning }: StatusMessageProps) => {
  const intl = useIntl();
  const { formatCurrencyWithoutDecimals } = useLocation();
  const { pendingChange, lastResolvedChange } = billLimit;

  if (pendingChange) {
    // Full phone on staff reads; fall back to the masked one from older backends
    const phone = pendingChange.phone ?? pendingChange.maskedPhone;
    const message =
      pendingChange.proposedMaxAmount == null
        ? intl.formatMessage({ id: 'digitalBill.billLimit.pendingRemoval' }, { phone })
        : intl.formatMessage(
            { id: 'digitalBill.billLimit.pending' },
            { amount: formatCurrencyWithoutDecimals(pendingChange.proposedMaxAmount), phone }
          );
    return <p className="text-sm">{message}</p>;
  }

  if (lastResolvedChange?.status === LastResolvedBillLimitChangeDtoStatus.REJECTED) {
    const message =
      lastResolvedChange.proposedMaxAmount == null
        ? intl.formatMessage({ id: 'digitalBill.billLimit.rejectedRemoval' })
        : intl.formatMessage(
            { id: 'digitalBill.billLimit.rejected' },
            { amount: formatCurrencyWithoutDecimals(lastResolvedChange.proposedMaxAmount) }
          );
    return <p className="text-sm text-orange-darkest">{message}</p>;
  }

  if (isMaxReached) {
    return <p className="text-sm">{intl.formatMessage({ id: 'digitalBill.billLimit.maxReached' })}</p>;
  }

  if (showWillExceedWarning) {
    return <p className="text-sm">{intl.formatMessage({ id: 'digitalBill.billLimit.orderWillExceed' })}</p>;
  }

  return null;
};

const BillLimitCard = ({
  billLimit,
  onEdit,
  onCancelRequest,
  isCancelRequestPending,
  showWillExceedWarning,
}: BillLimitCardProps) => {
  const intl = useIntl();
  const { formatAmountWithoutDecimals, formatCurrencyWithoutDecimals } = useLocation();
  const hasPendingChange = Boolean(billLimit.pendingChange);

  const consumedAmount = billLimit.consumedAmount ?? 0;
  const maxAmount = billLimit.maxAmount ?? 0;
  const remainingAmount = billLimit.remainingAmount ?? 0;
  const isMaxReached = remainingAmount <= 0;
  const percentage = toConsumedPercentage(consumedAmount, maxAmount);

  return (
    <section
      data-cy="bill-limit-card"
      className={classNames(
        'flex flex-col gap-4 rounded p-4',
        isMaxReached ? 'bg-orange-lightest' : 'bg-purple-lightest'
      )}
    >
      <div className="flex items-center gap-2">
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-bold">{intl.formatMessage({ id: 'digitalBill.billLimit.title' })}</span>
          <span className={classNames('text-xs', isMaxReached ? 'text-orange-darkest' : 'text-grey-dark')}>
            {intl.formatMessage(
              { id: 'digitalBill.billLimit.remaining' },
              { amount: formatCurrencyWithoutDecimals(remainingAmount) }
            )}
          </span>
        </div>
        <span
          className={classNames(
            'flex-1 text-right text-2xl font-bold tracking-tight',
            isMaxReached ? 'text-orange-darkest' : 'text-brand-primary'
          )}
        >
          {intl.formatMessage(
            { id: 'digitalBill.billLimit.consumedOfMax' },
            { consumed: formatAmountWithoutDecimals(consumedAmount), max: formatCurrencyWithoutDecimals(maxAmount) }
          )}
        </span>
      </div>
      <StatusMessage billLimit={billLimit} isMaxReached={isMaxReached} showWillExceedWarning={showWillExceedWarning} />
      <ProgressBar percentage={percentage} isMaxReached={isMaxReached} />
      {hasPendingChange ? (
        <Button
          kind="secondary"
          size="small"
          data-cy="cancel-bill-limit-request"
          className="self-start rounded-full"
          isDisabled={isCancelRequestPending}
          onClick={onCancelRequest}
        >
          {intl.formatMessage({ id: 'digitalBill.billLimit.cancelRequest' })}
        </Button>
      ) : (
        <Button kind="secondary" fullWidth data-cy="edit-bill-limit" className="font-bold" onClick={onEdit}>
          {intl.formatMessage({ id: 'digitalBill.billLimit.edit' })}
        </Button>
      )}
    </section>
  );
};

export default BillLimitCard;
